/**
 * `/quote/sequences/new` — multi-step follow-up sequence editor.
 *
 * Replaces the single-step inline form that previously lived on the
 * sequences list page. Lets a user assemble a sequence of up to 20 steps
 * (one to fit the schema max) with per-step delay + subject + body
 * templates. Submit POSTs through `useCreateQuoteSequence` and redirects
 * to the new sequence's detail view.
 *
 * Reorder is via up/down buttons rather than drag-and-drop — no DnD
 * library exists in the workspace today and rolling one in for this
 * page alone isn't worth it.
 */
import { useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useCreateQuoteSequence } from "@/hooks/use-quote";

interface DraftStep {
  // Stable client-side id used only for React keys + array operations.
  // The server assigns the real id on create.
  key: string;
  delay_hours: number;
  subject_template: string;
  body_template: string;
}

function makeKey(): string {
  return Math.random().toString(36).slice(2, 10);
}

function defaultFirstStep(): DraftStep {
  return {
    key: makeKey(),
    delay_hours: 24,
    subject_template: "Following up on your quote, {{customer_name}}",
    body_template:
      "Hi {{customer_name}},\n\nJust circling back on the quote we sent. Let me know if you have any questions.\n\n— {{business_name}}",
  };
}

function defaultExtraStep(stepNumber: number): DraftStep {
  return {
    key: makeKey(),
    delay_hours: 72,
    subject_template: `Step ${stepNumber} — checking in, {{customer_name}}`,
    body_template:
      "Hi {{customer_name}},\n\nWanted to check in once more — happy to walk through anything in the quote that's unclear.\n\n— {{business_name}}",
  };
}

const MAX_STEPS = 20;

export function QuoteSequenceNewPage() {
  const navigate = useNavigate();
  const createSeq = useCreateQuoteSequence();
  const nameId = useId();
  const descriptionId = useId();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [steps, setSteps] = useState<DraftStep[]>([defaultFirstStep()]);

  function updateStep(key: string, patch: Partial<Omit<DraftStep, "key">>) {
    setSteps((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  }

  function addStep() {
    setSteps((prev) => {
      if (prev.length >= MAX_STEPS) return prev;
      return [...prev, defaultExtraStep(prev.length + 1)];
    });
  }

  function removeStep(key: string) {
    setSteps((prev) => (prev.length <= 1 ? prev : prev.filter((s) => s.key !== key)));
  }

  function moveStep(key: string, direction: "up" | "down") {
    setSteps((prev) => {
      const idx = prev.findIndex((s) => s.key === key);
      if (idx < 0) return prev;
      const swapIdx = direction === "up" ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= prev.length) return prev;
      const copy = [...prev];
      [copy[idx], copy[swapIdx]] = [copy[swapIdx]!, copy[idx]!];
      return copy;
    });
  }

  function trimmedSteps(): {
    step_order: number;
    delay_hours: number;
    subject_template: string;
    body_template: string;
  }[] {
    return steps.map((s, i) => ({
      step_order: i + 1,
      delay_hours: Number.isFinite(s.delay_hours) && s.delay_hours >= 0 ? s.delay_hours : 0,
      subject_template: s.subject_template.trim(),
      body_template: s.body_template.trim(),
    }));
  }

  function isSubmittable(): boolean {
    if (!name.trim()) return false;
    if (steps.length === 0) return false;
    return steps.every((s) => s.subject_template.trim() !== "" && s.body_template.trim() !== "");
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!isSubmittable()) return;
    createSeq.mutate(
      {
        name: name.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        steps: trimmedSteps(),
      },
      {
        onSuccess: (sequence) => navigate(`/quote/sequences/${sequence.id}`),
      },
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Quote</p>
        <h1
          className="text-2xl font-extrabold tracking-tight text-gray-900"
          data-testid="quote-seq-new-heading"
        >
          New follow-up sequence
        </h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Multi-step drip. Each step produces a draft for operator approval before it sends.
          Variables: {"{{customer_name}}, {{business_name}}, {{sequence_name}}"}.{" "}
          <button
            type="button"
            className="underline-offset-2 hover:underline"
            onClick={() => navigate("/quote/sequences")}
            data-testid="quote-seq-new-back-link"
          >
            ← Back to sequences
          </button>
        </p>
      </header>

      <form onSubmit={submit} className="flex flex-col gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Details</CardTitle>
            <CardDescription>Name shows up in the lead detail page enrollment menu.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <label htmlFor={nameId} className="text-sm font-medium">
              Name
            </label>
            <input
              id={nameId}
              className="rounded border border-input bg-background px-3 py-2 text-sm"
              placeholder="e.g. 30-day stale-quote drip"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              data-testid="quote-seq-name-input"
            />
            <label htmlFor={descriptionId} className="text-sm font-medium">
              Description (optional)
            </label>
            <input
              id={descriptionId}
              className="rounded border border-input bg-background px-3 py-2 text-sm"
              placeholder="One line — when should this sequence be enrolled?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="quote-seq-description-input"
            />
          </CardContent>
        </Card>

        <section className="flex flex-col gap-3" data-testid="quote-seq-steps-section">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">
              Steps ({steps.length}/{MAX_STEPS})
            </h2>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addStep}
              disabled={steps.length >= MAX_STEPS}
              data-testid="quote-seq-add-step-cta"
            >
              + Add step
            </Button>
          </div>

          {steps.map((step, idx) => {
            const stepNumber = idx + 1;
            const isFirst = idx === 0;
            const isLast = idx === steps.length - 1;
            return (
              <Card key={step.key} data-testid={`quote-seq-step-${stepNumber}`}>
                <CardHeader>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <CardTitle className="text-base">Step {stepNumber}</CardTitle>
                      <CardDescription>
                        Drafts {step.delay_hours}h after{" "}
                        {isFirst ? "the lead is enrolled" : `step ${stepNumber - 1} sends`}.
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => moveStep(step.key, "up")}
                        disabled={isFirst}
                        data-testid={`quote-seq-step-${stepNumber}-up`}
                      >
                        ↑
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => moveStep(step.key, "down")}
                        disabled={isLast}
                        data-testid={`quote-seq-step-${stepNumber}-down`}
                      >
                        ↓
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeStep(step.key)}
                        disabled={steps.length <= 1}
                        data-testid={`quote-seq-step-${stepNumber}-remove`}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="flex flex-col gap-3">
                  <label className="text-sm font-medium">
                    Delay (hours)
                    <input
                      type="number"
                      min={0}
                      max={720}
                      className="mt-1 block w-32 rounded border border-input bg-background px-3 py-2 text-sm"
                      value={step.delay_hours}
                      onChange={(e) =>
                        updateStep(step.key, {
                          delay_hours: Number.parseInt(e.target.value || "0", 10),
                        })
                      }
                      data-testid={`quote-seq-step-${stepNumber}-delay`}
                    />
                  </label>
                  <label className="text-sm font-medium">
                    Subject template
                    <input
                      className="mt-1 block w-full rounded border border-input bg-background px-3 py-2 text-sm"
                      value={step.subject_template}
                      onChange={(e) => updateStep(step.key, { subject_template: e.target.value })}
                      required
                      data-testid={`quote-seq-step-${stepNumber}-subject`}
                    />
                  </label>
                  <label className="text-sm font-medium">
                    Body template
                    <textarea
                      className="mt-1 block w-full min-h-[140px] rounded border border-input bg-background px-3 py-2 text-sm"
                      value={step.body_template}
                      onChange={(e) => updateStep(step.key, { body_template: e.target.value })}
                      required
                      data-testid={`quote-seq-step-${stepNumber}-body`}
                    />
                  </label>
                </CardContent>
              </Card>
            );
          })}
        </section>

        <div className="flex items-center gap-3">
          <Button
            type="submit"
            disabled={!isSubmittable() || createSeq.isPending}
            data-testid="quote-seq-submit-cta"
          >
            {createSeq.isPending ? "Creating…" : "Create sequence"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/quote/sequences")}
            data-testid="quote-seq-cancel-cta"
          >
            Cancel
          </Button>
          {createSeq.error && (
            <span className="text-sm text-destructive" data-testid="quote-seq-submit-error">
              {createSeq.error.message}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

export default QuoteSequenceNewPage;
